import type { StorageLike, UseStorageOptions } from "@vueuse/core";
import {
  defaultWindow,
  getSSRHandler,
  pausableWatch,
  resolveUnref,
  StorageSerializers,
  useEventListener,
} from "@vueuse/core";
import type { RemovableRef } from "@vueuse/shared";
import { computed, ref, shallowRef } from "vue";
import { isFunction, isString, isUndefinedOrNull } from "@/utils";

export function guessSerializerType<T extends string | number | boolean | object | null>(
  rawInit: T
): "any" | "set" | "map" | "date" | "boolean" | "string" | "object" | "number" {
  return rawInit == null
    ? "any"
    : rawInit instanceof Set
    ? "set"
    : rawInit instanceof Map
    ? "map"
    : rawInit instanceof Date
    ? "date"
    : typeof rawInit === "boolean"
    ? "boolean"
    : typeof rawInit === "string"
    ? "string"
    : typeof rawInit === "object"
    ? "object"
    : Array.isArray(rawInit)
    ? "object"
    : !Number.isNaN(rawInit)
    ? "number"
    : "any";
}

export default {
  get(name: string): string | null {
    return localStorage.getItem(name);
  },
  set(name: string, value: string | undefined | null): void {
    if (value === undefined || value === null) {
      this.remove(name);
    } else {
      localStorage.setItem(name, value);
    }
  },
  remove(name: string): void {
    localStorage.removeItem(name);
  },
  getObj<T extends string | number | boolean | object | null>(name: string, defVal: T | null = null): T | null {
    const type = guessSerializerType<T>(defVal as T);
    const serializer = StorageSerializers[type];
    const data = this.get(name);
    if (data === null) return defVal;
    //return JSON.parse(data) as T;
    return serializer.read(data) as T;
  },
  setObj<T extends string | number | boolean | object | null>(name: string, value: T): void {
    const type = guessSerializerType<T>(value);
    const serializer = StorageSerializers[type];
    //this.set(name, JSON.stringify(value));
    this.set(name, serializer.write(value));
  },
};
export function useLocalStorage<T extends string | number | boolean | object | null>(
  getKey: () => string,
  initialValue: T,
  options: UseStorageOptions<T> = {}
): RemovableRef<unknown> {
  const { window = defaultWindow } = options;
  return useStorage(getKey, initialValue, window?.localStorage, options);
}

export function useStorage<T extends string | number | boolean | object | null>(
  getKey: () => string,
  defaults: T,
  storage: StorageLike | undefined,
  options: UseStorageOptions<T> = {}
): RemovableRef<T> {
  const {
    flush = "pre",
    deep = true,
    listenToStorageChanges = true,
    writeDefaults = true,
    mergeDefaults = false,
    shallow,
    window = defaultWindow,
    eventFilter,
    onError = (e) => {
      console.error(e);
    },
  } = options;

  const data = (shallow ? shallowRef : ref)(defaults) as RemovableRef<T>;

  if (!storage) {
    try {
      storage = getSSRHandler("getDefaultStorage", () => defaultWindow?.localStorage)();
    } catch (e) {
      onError(e);
    }
  }

  if (!storage) throw new Error("no storage");

  const rawInit: T = resolveUnref(defaults);
  const type = guessSerializerType<T>(rawInit);
  const serializer = options.serializer ?? StorageSerializers[type];

  const { pause: pauseWatch, resume: resumeWatch } = pausableWatch(
    computed(() => [data, getKey()]),
    (ev) => {
      console.debug("useStorage pausableWatch", ev);
      write(data.value, "watch");
    },
    {
      flush,
      deep,
      eventFilter,
    }
  );

  if (window && listenToStorageChanges) {
    useEventListener(window, "storage", (ev) => update(ev, "useEventListener"));
  }

  update(undefined, "main");

  return data;

  function write(v: never, source: string | undefined = undefined) {
    const key = getKey();
    try {
      if (isUndefinedOrNull(v)) storage?.removeItem(key);
      const value = serializer.write(v);
      console.debug("useStorage.write", { key, value, source, storage });
      storage?.setItem(key, value);
    } catch (e) {
      onError(e);
    }
  }

  function read(event?: StorageEvent) {
    const key = getKey();
    if (event && event.key !== key) return;

    pauseWatch();
    try {
      const rawValue = event ? event.newValue : storage?.getItem(key);
      if (rawValue == null) {
        if (writeDefaults && rawInit !== null) storage?.setItem(key, serializer.write(rawInit));
        return rawInit;
      } else if (!event && mergeDefaults) {
        const value = serializer.read(rawValue);
        if (isFunction(mergeDefaults)) return mergeDefaults(value, rawInit);
        else if (type === "object" && !Array.isArray(value)) return { ...(rawInit as never[]), ...value };
        console.debug("useStorage.read return", { event, key, type: "1", value });
        return value;
      } else if (!isString(rawValue)) {
        console.debug("useStorage.read return", { event, key, type: "string", rawValue });
        return rawValue;
      } else {
        const value = serializer.read(rawValue);
        console.debug("useStorage.read return", { event, key, type: "serialized", value });
        return value;
      }
    } catch (e) {
      console.error("useStorage.read", { event, key, e });
      onError(e);
    } finally {
      resumeWatch();
    }
  }

  function update(event?: StorageEvent, source?: string) {
    const key = getKey();
    if (event && event.key !== key) {
      console.warn("useStorage.update skipped", event, key);
      return;
    }
    data.value = read(event);
    console.log("useStorage.update", { event, key, value: data.value, source });
  }
}
